export type PromptAttachment = {
  id: string;
  name: string;
  size: number;
  mimeType?: string;
  /** Base64-encoded content (data URL prefix stripped). */
  content: string;
};

export const PROMPT_ATTACHMENT_ACCEPT = ".pdf,.docx,.xlsx,.xlsm,.xls,.csv,.tsv,.txt,.md";

export const MAX_PROMPT_ATTACHMENTS = 4;

export const MAX_PROMPT_ATTACHMENT_BYTES = 12 * 1024 * 1024;

const SUPPORTED_EXTENSIONS = PROMPT_ATTACHMENT_ACCEPT.split(",");

export const isSupportedPromptAttachment = (name: string): boolean => {
  const normalized = name.trim().toLowerCase();
  return SUPPORTED_EXTENSIONS.some((extension) => normalized.endsWith(extension));
};

export const formatAttachmentSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${bytes} B`;
};

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const separator = result.indexOf("base64,");
      resolve(separator >= 0 ? result.slice(separator + "base64,".length) : result);
    };
    reader.readAsDataURL(file);
  });

export const buildPromptAttachment = async (file: File): Promise<PromptAttachment> => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  name: file.name,
  size: file.size,
  mimeType: file.type || undefined,
  content: await readFileAsBase64(file),
});

/** Returns an error message when the file cannot be attached, null when it can. */
export const promptAttachmentError = (
  file: File,
  currentCount: number
): string | null => {
  if (currentCount >= MAX_PROMPT_ATTACHMENTS) {
    return `You can attach up to ${MAX_PROMPT_ATTACHMENTS} files.`;
  }
  if (!isSupportedPromptAttachment(file.name)) {
    return `${file.name} is not supported. Use PDF, Word, Excel, CSV, or text files.`;
  }
  if (file.size > MAX_PROMPT_ATTACHMENT_BYTES) {
    return `${file.name} is larger than ${formatAttachmentSize(MAX_PROMPT_ATTACHMENT_BYTES)}.`;
  }
  return null;
};
